import { Big } from 'big.js';
import { type Position, type PositionType, PositionTypeLong, PositionTypeShort } from '@/api/spec';
import { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import { amountToBig } from '@/utils/bigUtils';
import { generateBlockLogId } from '@/utils/generateBlockLogId';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';
import { leverageFromHundredths } from '@/utils/leverageUtils';
import { type PositionCore, PositionStatus, type ViewPosition } from './types';

export type BasePositionData = PositionCore & {
  collateral: Big;
  fee: Big;
  closeFee: Big; // `cfee` — what the close/decrease itself paid; zero where none was charged
  positionValueAtEntry: Big;
  openTimestamp: number;
  atTimestamp: number;
};

// Open positions' equity contribution, Σ(PnL + margin). Shared by Account Value and the maxEquity
// deposit cap so the two can't drift.
export function sumOpenPositionsEquity(positions: readonly ViewPosition[]): Big {
  return positions
    .filter((p) => p.status === PositionStatus.open)
    .reduce((sum, p) => sum.plus(p.pnl || 0).plus(p.margin || 0), new Big(0));
}

export const mapPositionTypeToSide = (positionType: PositionType): Side | null => {
  switch (positionType) {
    case PositionTypeLong:
      return Side.long;
    case PositionTypeShort:
      return Side.short;
    default:
      return null;
  }
};

export function toBasePosition(p: Position, market: MarketData): BasePositionData | null {
  const side = mapPositionTypeToSide(p.sd);
  if (!side) return null;

  const assetDecimals = market.assetDecimals ?? 4;
  const quoteDecimals = market.quoteDecimals ?? 2;

  const size = p.s ? offsetNumberToNumber(p.s, assetDecimals) : 0;
  const entryPrice = p.ep ? offsetNumberToNumber(p.ep, quoteDecimals) : 0;
  const collateral = amountToBig(p.c, market.quote.decimals);
  const fee = amountToBig(p.fee, market.quote.decimals);
  const closeFee = p.cfee ? amountToBig(p.cfee, market.quote.decimals) : new Big(0);

  const marketId = p.mkt;
  const positionId = Number(p.pid);
  const assetCurrency = market.symbol || '';
  const openTimestamp = p.ots?.t ?? 0;
  const atTimestamp = p.at?.t ?? 0;

  const uniqueId = `${p.mkt}-${p.pid}-${p.at ? generateBlockLogId(p.at) : 0}`;
  const txHash = p.at?.txid;

  const positionValueAtEntry = new Big(size).times(entryPrice);

  return {
    marketId,
    side,
    assetCurrency,
    assetDecimals,
    quoteDecimals,
    size,
    entryPrice,
    initialLeverage: leverageFromHundredths(p.lv),
    positionId,
    txHash,
    collateral,
    fee,
    closeFee,
    positionValueAtEntry,
    openTimestamp,
    atTimestamp,
    uniqueId,
  };
}
